import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  getSectionFlags,
  getSectionsForApplication,
  type LoanSectionKey,
  type LoanSectionViewerContext,
} from '@/config/loanProductRegistry';
import {
  useGetLoanApplicationQuery,
  usePatchLoanFocusProductMutation,
  usePatchLoanProductTypeMutation,
  useSubmitLoanApplicationMutation,
  useWithdrawLoanApplicationMutation,
} from '@/apis/extended/loanApplicationApi';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { useAuth } from '@/hooks/useAuth';
import { loanAttestationNeedsRecording } from '@/utils/loanApplicationAttestation';
import {
  linkedProductLabel,
  resolveProductId,
} from '@/utils/loanApplicationHub';
import type { PropertyView } from '@/types/PropertyView';
import { AgencyLoanTermsSection } from './AgencyLoanTermsSection';
import { AssetsSection } from './AssetsSection';
import { BorrowerSection } from './BorrowerSection';
import { ChecklistSection } from './ChecklistSection';
import { DeclarationsSection } from './DeclarationsSection';
import { DemographicsSection } from './DemographicsSection';
import { DscrSection } from './DscrSection';
import { EmploymentSection } from './EmploymentSection';
import { LiabilitiesSection } from './LiabilitiesSection';
import {
  LoanSectionFormProvider,
  useLoanSectionFormNav,
} from './LoanSectionFormContext';
import { PlaceholderSection } from './PlaceholderSection';
import { PropertySection } from './PropertySection';
import { SignSection } from './SignSection';
import { TrackRecordSection } from './TrackRecordSection';

type Props = {
  applicationId: string;
  /** Shown inside admin detail — tighter chrome */
  variant?: 'full' | 'embedded';
  /** Active deal-room property when embedded in a property stage */
  propertyContext?: PropertyView | null;
};

export function LoanApplicationShell(props: Props) {
  return (
    <LoanSectionFormProvider>
      <LoanApplicationShellInner {...props} />
    </LoanSectionFormProvider>
  );
}

function LoanApplicationShellInner({
  applicationId,
  variant = 'full',
  propertyContext,
}: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFocusSyncRef = useRef<string | null>(null);
  useEffect(() => {
    urlFocusSyncRef.current = null;
    setSelected(null);
  }, [applicationId]);
  const { isAdmin, user } = useAuth();
  const {
    data: application,
    isLoading,
    error,
  } = useGetLoanApplicationQuery(applicationId);
  const [submit, { isLoading: submitting }] =
    useSubmitLoanApplicationMutation();
  const [withdraw, { isLoading: withdrawing }] =
    useWithdrawLoanApplicationMutation();
  const [patchFocus] = usePatchLoanFocusProductMutation();
  const [patchProductType] = usePatchLoanProductTypeMutation();

  const [selected, setSelected] = useState<LoanSectionKey | null>(null);
  const embedded = variant === 'embedded';
  const confirmDiscard = useLoanSectionFormNav();
  const focusQueryParam = searchParams.get('focus');
  const sectionQueryParam = searchParams.get(
    'section'
  ) as LoanSectionKey | null;

  const selectSection = (key: LoanSectionKey) => {
    if (key === selected) return;
    if (!confirmDiscard()) return;
    setSelected(key);
  };

  const focusedProductId = useMemo(() => {
    if (!application) return '';
    return (
      resolveProductId(propertyContext?.productId ?? propertyContext?.id) ||
      resolveProductId(focusQueryParam) ||
      resolveProductId(application.focusProductId) ||
      resolveProductId(application.sourceProductId)
    );
  }, [application, propertyContext, focusQueryParam]);

  const sectionViewer: LoanSectionViewerContext = useMemo(
    () => ({
      loanEstimationType: user?.loanEstimationType,
      bypassInvestorGate: isAdmin,
      standaloneShell: !embedded,
      showPurchasePropertySection: Boolean(
        embedded ||
          focusQueryParam ||
          resolveProductId(application?.focusProductId) ||
          resolveProductId(application?.sourceProductId) ||
          propertyContext
      ),
    }),
    [
      user?.loanEstimationType,
      isAdmin,
      embedded,
      focusQueryParam,
      application?.focusProductId,
      application?.sourceProductId,
      propertyContext,
    ]
  );

  const sections = useMemo(
    () =>
      application ? getSectionsForApplication(application, sectionViewer) : [],
    [application, sectionViewer]
  );

  const sectionFlags = useMemo(
    () => (application ? getSectionFlags(application, sectionViewer) : null),
    [application, sectionViewer]
  );

  useEffect(() => {
    if (!sections.length) return;
    setSelected((cur) => {
      if (cur && sections.some((s) => s.key === cur)) return cur;
      if (
        sectionQueryParam &&
        sections.some((s) => s.key === sectionQueryParam)
      ) {
        return sectionQueryParam;
      }
      return sections[0]?.key ?? 'borrower';
    });
  }, [sections, sectionQueryParam]);

  const borrowerMayEdit =
    application != null &&
    (application.status === 'draft' ||
      application.status === 'declined' ||
      (Boolean(application.borrowerEditsUnlocked) &&
        (application.status === 'submitted' ||
          application.status === 'in_review')));

  const readOnly = !isAdmin && application != null && !borrowerMayEdit;

  useEffect(() => {
    const want = focusQueryParam;
    if (!application || !want || readOnly) return;
    const cur = resolveProductId(application.focusProductId);
    if (cur === want) {
      urlFocusSyncRef.current = want;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('focus');
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (urlFocusSyncRef.current === want) return;
    urlFocusSyncRef.current = want;
    void patchFocus({ id: application._id, focusProductId: want })
      .unwrap()
      .then(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('focus');
            return next;
          },
          { replace: true }
        );
      })
      .catch(() => {
        urlFocusSyncRef.current = null;
        toast.error('Could not switch to that property.');
      });
  }, [application, readOnly, focusQueryParam, setSearchParams, patchFocus]);

  const activeSection = selected ?? sections[0]?.key ?? 'borrower';

  const currentLabel =
    sections.find((s) => s.key === activeSection)?.label ?? 'Section';

  const propertySectionVariant = useMemo(() => {
    if (!application) return 'standalone' as const;
    if (
      embedded ||
      application.flowProfile === 'refinance' ||
      focusedProductId
    ) {
      return 'propertyScoped' as const;
    }
    return 'standalone' as const;
  }, [application, embedded, focusedProductId]);

  const renderPanel = () => {
    if (!application) return null;
    switch (activeSection) {
      case 'property':
        return (
          <PropertySection
            application={application}
            readOnly={readOnly}
            propertyContext={propertyContext}
            variant={propertySectionVariant}
          />
        );
      case 'borrower':
        return (
          <BorrowerSection
            application={application}
            which="primary"
            readOnly={readOnly}
            showEntityQuestion={sectionFlags?.showEntityQuestionPrimary ?? true}
            onCoBorrowerAdded={() => setSelected('coBorrower')}
          />
        );
      case 'coBorrower':
        return (
          <BorrowerSection
            application={application}
            which="coBorrower"
            readOnly={readOnly}
            onCoBorrowerRemoved={() => setSelected('borrower')}
          />
        );
      case 'assets':
        return <AssetsSection application={application} readOnly={readOnly} />;
      case 'track':
        return (
          <TrackRecordSection application={application} readOnly={readOnly} />
        );
      case 'employment':
        return (
          <EmploymentSection application={application} readOnly={readOnly} />
        );
      case 'declarations':
        return (
          <DeclarationsSection application={application} readOnly={readOnly} />
        );
      case 'loanTerms':
        return (
          <AgencyLoanTermsSection
            application={application}
            readOnly={readOnly}
          />
        );
      case 'liabilities':
        return (
          <LiabilitiesSection application={application} readOnly={readOnly} />
        );
      case 'demographics':
        return (
          <DemographicsSection application={application} readOnly={readOnly} />
        );
      case 'dscr':
        if (application.productType !== 'dscr') {
          return (
            <PlaceholderSection
              title="DSCR"
              description="Not applicable for this product."
            />
          );
        }
        return <DscrSection application={application} readOnly={readOnly} />;
      case 'checklist':
        return (
          <ChecklistSection application={application} readOnly={readOnly} />
        );
      case 'sign':
        return <SignSection application={application} readOnly={readOnly} />;
      default:
        return <PlaceholderSection title={currentLabel} />;
    }
  };

  if (isLoading && !application) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading application…</Typography>
      </Box>
    );
  }

  if (error || !application) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Could not load this application. Return to the hub and try again.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/loan')}>
          Back to hub
        </Button>
      </Box>
    );
  }

  const chrome = variant === 'full';

  return (
    <Box
      sx={{
        maxWidth: chrome ? 1320 : '100%',
        mx: chrome ? 'auto' : 0,
        px: chrome ? 2 : 0,
        py: chrome ? 3 : 0,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {chrome && (
        <TastePageHeader
          title="Loan application"
          subtitle="Complete each section before submitting to underwriting."
          actions={
            <>
              <Chip
                label={application.productType?.toUpperCase()}
                size="small"
              />
              {application.flowProfile && (
                <Chip
                  label={application.flowProfile.replace(/_/g, ' ')}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={application.status?.replace(/_/g, ' ')}
                size="small"
              />
              {application.lifecycleStage &&
                application.lifecycleStage !== 'shopping' && (
                  <Chip
                    label={application.lifecycleStage.replace(/_/g, ' ')}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              <Button variant="text" onClick={() => navigate('/loan')}>
                All applications
              </Button>
            </>
          }
        />
      )}

      {chrome &&
        application.flowProfile === 'purchase' &&
        application.productType === 'dscr' &&
        !readOnly &&
        application.status === 'draft' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This purchase file is still marked as DSCR from an older flow. For
            Conventional, FHA, VA, or USDA, switch the loan program—we will open
            the URLA-style sections.
            <Button
              sx={{ ml: 2 }}
              size="small"
              variant="contained"
              onClick={() => {
                void patchProductType({
                  id: application._id,
                  productType: 'conventional',
                })
                  .unwrap()
                  .then(() => {
                    toast.success(
                      'Switched to Conventional — complete Loan details.'
                    );
                  })
                  .catch((e: unknown) => {
                    const msg =
                      e && typeof e === 'object' && 'data' in e
                        ? String(
                            (e as { data?: { error?: string } }).data?.error
                          )
                        : 'Update failed';
                    toast.error(msg);
                  });
              }}
            >
              Use residential mortgage (Conventional)
            </Button>
          </Alert>
        )}

      {chrome &&
        (application.flowProfile === 'purchase' ||
          application.flowProfile === 'investor_dscr') &&
        Array.isArray(application.linkedPurchaseProductIds) &&
        application.linkedPurchaseProductIds.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Subject property:
            </Typography>
            {application.linkedPurchaseProductIds.map((p) => {
              const pid = resolveProductId(p);
              const focused =
                resolveProductId(application.focusProductId) === pid;
              return (
                <Chip
                  key={pid}
                  label={linkedProductLabel(p)}
                  size="small"
                  color={focused ? 'primary' : 'default'}
                  variant={focused ? 'filled' : 'outlined'}
                  onClick={() => {
                    if (readOnly || !pid) return;
                    void patchFocus({
                      id: application._id,
                      focusProductId: pid,
                    })
                      .unwrap()
                      .catch(() => {
                        toast.error('Could not switch property');
                      });
                  }}
                  sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                />
              );
            })}
          </Box>
        )}

      {!readOnly && chrome && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Borrower, assets, employment, declarations, liabilities, and
          demographics are saved to your profile and pre-filled on your other
          purchase, refinance, and investment applications (property-specific
          sections stay per listing).
        </Alert>
      )}

      {readOnly && chrome && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This file is read-only while it is in progress with underwriting.
        </Alert>
      )}

      {!readOnly &&
        chrome &&
        Boolean(application.borrowerEditsUnlocked) &&
        (application.status === 'submitted' ||
          application.status === 'in_review') && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your loan team has turned editing back on for this file. Update any
            sections they requested, then keep an eye on your email for next
            steps.
          </Alert>
        )}

      {!readOnly &&
        chrome &&
        loanAttestationNeedsRecording(application) &&
        activeSection !== 'sign' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This application needs a current electronic attestation after your
            latest edits. Open <strong>Attestation</strong> in the sidebar when
            you are ready — nothing else is blocked.
          </Alert>
        )}

      {!isAdmin && !readOnly && application.status === 'draft' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={async () => {
              const blockingDocs = (application.checklist || []).filter(
                (i) =>
                  i.requiredForSubmit !== false &&
                  (i.requiredForSubmit === true || i.required !== false) &&
                  !i.completed
              );
              if (blockingDocs.length > 0) {
                toast.error(
                  `Complete required documents first: ${blockingDocs
                    .slice(0, 3)
                    .map((d) => d.label)
                    .join('; ')}`
                );
                return;
              }
              if (!application.signAttestation?.acceptedAt) {
                toast.error(
                  'Open Attestation and sign before submitting to underwriting'
                );
                return;
              }
              try {
                await submit(application._id).unwrap();
                toast.success('Submitted');
              } catch (e: unknown) {
                const msg =
                  e && typeof e === 'object' && 'data' in e
                    ? String((e as { data?: { error?: string } }).data?.error)
                    : 'Submit failed';
                toast.error(msg || 'Submit failed');
              }
            }}
          >
            Submit to underwriting
          </Button>
          {!isAdmin && (
            <Button
              variant="outlined"
              color="warning"
              disabled={withdrawing}
              onClick={async () => {
                try {
                  await withdraw(application._id).unwrap();
                  toast.success('Withdrawn');
                } catch {
                  toast.error('Withdraw failed');
                }
              }}
            >
              Withdraw
            </Button>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: embedded
            ? { xs: '1fr', sm: 'minmax(0, 220px) minmax(0, 1fr)' }
            : { xs: '1fr', md: '260px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'start',
          minWidth: 0,
          width: '100%',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
            minWidth: 0,
            maxHeight: embedded ? { xs: 280, sm: 'none' } : undefined,
            overflowY: embedded ? { xs: 'auto', sm: 'hidden' } : undefined,
          }}
        >
          <List dense disablePadding>
            {sections.map((s, i) => {
              const attestReminder =
                s.key === 'sign' && loanAttestationNeedsRecording(application);
              return (
                <ListItemButton
                  key={s.key}
                  selected={activeSection === s.key}
                  onClick={() => selectSection(s.key)}
                >
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span>{`${i + 1}. ${s.label}`}</span>
                        {attestReminder && (
                          <Chip
                            size="small"
                            color="warning"
                            label="Due"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: 3, minHeight: 320, minWidth: 0, overflow: 'hidden' }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {currentLabel}
          </Typography>
          {renderPanel()}
        </Paper>
      </Box>
    </Box>
  );
}
